import { useState } from 'react';
import { C } from '../theme/palette';
import { dansUneAutreApp, estIos, navigateurIos, useInstallation } from './installation';
import { NOM_LOGICIEL } from '../lib/marque';

/**
 * « Mettre SwissTech sur mon téléphone » : l'application s'installe comme les
 * autres, avec son icône sur l'écran d'accueil, pour ne plus chercher le lien.
 *
 * <p>Android (Chrome) : un seul bouton, la proposition du téléphone. iPhone :
 * Safari ne propose rien, on montre les trois gestes — et, dans WhatsApp ou une
 * autre application, d'abord « Ouvrir dans Safari ». Une fois installée, la
 * carte ne s'affiche plus.
 *
 * @param {object} props propriétés
 * @param {boolean} [props.vous] vouvoie (page remise à un client) plutôt que tutoyer
 * @param {string} [props.nom] nom sous l'icône
 * @param {string} [props.apres] ce que l'icône ouvrira, dit en une phrase
 * @param {boolean} [props.replie] n'affiche d'abord qu'un bandeau à toucher
 * @returns {import('react').ReactElement|null} le guide
 */
export function GuideInstallation({
  vous = false,
  nom = NOM_LOGICIEL,
  apres = '',
  replie = false,
}) {
  const { proposable, installee, installer } = useInstallation();
  const [ouvert, setOuvert] = useState(!replie);
  const [refuse, setRefuse] = useState(false);
  if (installee) return null;
  const t = (tu, vs) => (vous ? vs : tu);
  const ios = estIos();
  const autreApp = dansUneAutreApp();
  const navigateur = navigateurIos();
  // Où trouver « Partager » : en bas dans Safari, en haut à droite dans
  // Chrome, dans le menu ≡ de Firefox.
  const partager =
    navigateur === 'chrome' ? (
      <>
        {t('Touche', 'Touchez')} le bouton <b>Partager</b>{' '}
        <span aria-label="Partager" style={{ fontSize: 16 }}>
          ⬆︎
        </span>{' '}
        <b>en haut à droite</b>, dans la barre d’adresse de Chrome.
      </>
    ) : navigateur === 'firefox' ? (
      <>
        {t('Touche', 'Touchez')} le menu <b>≡</b> en bas à droite de Firefox, puis <b>Partager</b>.
      </>
    ) : navigateur === 'edge' ? (
      <>
        {t('Touche', 'Touchez')} le menu <b>•••</b> en bas d’Edge, puis <b>Partager</b>{' '}
        <span aria-label="Partager" style={{ fontSize: 16 }}>
          ⬆︎
        </span>
        .
      </>
    ) : (
      <>
        {t('Touche', 'Touchez')} le bouton <b>Partager</b>{' '}
        <span aria-label="Partager" style={{ fontSize: 16 }}>
          ⬆︎
        </span>{' '}
        (le carré avec une flèche, en bas de Safari).
      </>
    );

  const cadre = {
    background: '#fff',
    border: '1.5px solid ' + C.teal,
    borderRadius: 14,
    padding: 14,
    margin: '12px 0',
    textAlign: 'left',
    color: C.ink,
  };
  const etape = {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    margin: '9px 0',
    fontSize: 13,
    lineHeight: 1.45,
  };
  const pastille = {
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: 12,
    background: C.teal,
    color: '#fff',
    fontWeight: 800,
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        style={{
          ...cadre,
          width: '100%',
          fontSize: 13,
          fontWeight: 800,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        📲 {t('Mettre', 'Mettre')} {nom} {t('sur ton écran d’accueil', 'sur votre écran d’accueil')}{' '}
        ›
      </button>
    );
  }

  return (
    <div style={cadre} data-testid="guide-installation">
      <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 4 }}>
        📲 {t('Mets', 'Mettez')} {nom} {t('sur ton téléphone', 'sur votre téléphone')}
      </div>
      <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.45, marginBottom: 6 }}>
        {t(
          'Une icône sur ton écran d’accueil, comme une vraie application : plus besoin de chercher le lien.',
          'Une icône sur votre écran d’accueil, comme une vraie application : plus besoin de chercher le lien.',
        )}{' '}
        {apres}
      </div>

      {proposable ? (
        <>
          <button
            type="button"
            onClick={async () => {
              const ok = await installer();
              if (!ok) setRefuse(true);
            }}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 12,
              padding: '14px 12px',
              background: C.teal,
              color: '#fff',
              fontSize: 14,
              fontWeight: 900,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            📲 {t('Installer', 'Installer')} {nom}
          </button>
          {refuse && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
              {t(
                'Tu pourras l’installer plus tard depuis cette page.',
                'Vous pourrez l’installer plus tard depuis cette page.',
              )}
            </div>
          )}
        </>
      ) : ios && autreApp ? (
        <div style={etape}>
          <span style={pastille}>1</span>
          <span>
            {t('Ouvre', 'Ouvrez')} d’abord ce lien dans <b>Safari</b> : {t('touche', 'touchez')}{' '}
            <b>•••</b> ou <b>⬆︎</b> en haut ou en bas, puis <b>« Ouvrir dans Safari »</b>.{' '}
            {t('Reviens', 'Revenez')} ensuite à cette page.
          </span>
        </div>
      ) : ios ? (
        <>
          <div style={etape}>
            <span style={pastille}>1</span>
            <span>{partager}</span>
          </div>
          <div style={etape}>
            <span style={pastille}>2</span>
            <span>
              {t('Choisis', 'Choisissez')} <b>« Sur l’écran d’accueil »</b> (ou{' '}
              <b>« Ajouter à l’écran d’accueil »</b> ;{' '}
              {t('fais défiler la liste si besoin', 'faites défiler la liste si besoin')}).
            </span>
          </div>
          <div style={etape}>
            <span style={pastille}>3</span>
            <span>
              {t('Touche', 'Touchez')} <b>« Ajouter »</b> en haut à droite. L’icône <b>{nom}</b>{' '}
              apparaît à côté {t('de tes', 'de vos')} autres applications.
            </span>
          </div>
        </>
      ) : (
        <div style={etape}>
          <span style={pastille}>1</span>
          <span>
            Dans le menu <b>⋮</b> du navigateur, {t('choisis', 'choisissez')}{' '}
            <b>« Installer l’application »</b> ou <b>« Ajouter à l’écran d’accueil »</b>.
          </span>
        </div>
      )}
    </div>
  );
}
